import { Stack, Tab, Tabs } from '@mui/material';
import { MemoRoomLevelIssuesTab } from 'app/pages/ValidationsPage/components/modules/RoomIssues';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getIssuesConfig } from 'services/option';
import { resolveRoomIssueIds } from 'services/report';
import { DEFAULT_ISSUES_UI_CONFIG, IssuesUiConfig } from 'utils/issueDueDateColor';
import { RoomLevelIssues } from 'types/room';
import { useIssuesRoomTab } from '../hooks/useIssuesRoomTab';
import { RoomIssueDialog } from './RoomIssueDialog';

type IssuesRoomTabContentProps = {
  view: 'active' | 'archived';
};

const IssuesRoomTabContent = ({ view }: IssuesRoomTabContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const roomIssueIdParam = searchParams.get('roomIssueId');
  const submitIdParam = searchParams.get('submitId');
  const [dialogIssueId, setDialogIssueId] = useState<number | null>(null);
  const [dialogSubmitIdHint, setDialogSubmitIdHint] = useState<number | undefined>(undefined);
  const [dialogClientIdHint, setDialogClientIdHint] = useState<number | undefined>(undefined);
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);

  const resolveClientIdFromIssue = useCallback((issue: RoomLevelIssues) => {
    const fromClient = issue.room?.client?.id;
    if (fromClient != null) return Number(fromClient);
    const fromRoom = issue.room?.clientId;
    if (fromRoom != null) return Number(fromRoom);
    return undefined;
  }, []);

  const {
    options,
    dataRoomLevelIssues,
    pagingInfoRoomLevelIssues,
    handleChangePage,
    handleSort,
    sortOption,
    onSearch,
    searchText,
    filterRoomIssues,
    setFilterRoomIssues,
    searchType,
    setSearchType,
    asyncExportRoomIssues,
    refreshDataRoomLevelIssues
  } = useIssuesRoomTab(view);

  useEffect(() => {
    getIssuesConfig()
      .then((res) => setIssuesConfig({ ...DEFAULT_ISSUES_UI_CONFIG, ...(res?.config || {}) }))
      .catch(() => setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG));
  }, []);

  useEffect(() => {
    const issueId = roomIssueIdParam ? Number(roomIssueIdParam) : NaN;
    const submitId = submitIdParam ? Number(submitIdParam) : NaN;
    if (!Number.isNaN(issueId)) {
      setDialogIssueId(issueId);
      setDialogSubmitIdHint(Number.isNaN(submitId) ? undefined : submitId);
      const fromList = dataRoomLevelIssues.find((item) => Number(item.id) === issueId);
      setDialogClientIdHint(fromList ? resolveClientIdFromIssue(fromList) : undefined);
    }
  }, [roomIssueIdParam, submitIdParam, dataRoomLevelIssues, resolveClientIdFromIssue]);

  const openAssignment = useCallback(
    (issue: RoomLevelIssues) => {
      const ids = resolveRoomIssueIds(issue);
      if (!ids) return;
      setDialogIssueId(ids.issueId);
      setDialogSubmitIdHint(ids.submitId);
      setDialogClientIdHint(resolveClientIdFromIssue(issue));
      const params: Record<string, string> = {
        tab: 'roomIssues',
        roomIssueId: String(ids.issueId)
      };
      if (ids.submitId) params.submitId = String(ids.submitId);
      setSearchParams(params);
    },
    [setSearchParams, resolveClientIdFromIssue]
  );

  const closeDialog = useCallback(() => {
    setDialogIssueId(null);
    setDialogSubmitIdHint(undefined);
    setDialogClientIdHint(undefined);
    setSearchParams({ tab: 'roomIssues' });
  }, [setSearchParams]);

  return (
    <>
      <MemoRoomLevelIssuesTab
        assignmentMode
        issuesConfig={issuesConfig}
        dataRoomLevelIssues={dataRoomLevelIssues}
        pagingInfo={pagingInfoRoomLevelIssues}
        handleChangePage={handleChangePage}
        handleSort={handleSort}
        sortOption={sortOption}
        onSearch={onSearch}
        initSearchText={searchText}
        setFilterRoomIssues={setFilterRoomIssues}
        filterRoomIssues={filterRoomIssues}
        options={options}
        searchType={searchType}
        setSearchType={setSearchType}
        onExport={asyncExportRoomIssues}
        refreshDataRoomLevelIssues={refreshDataRoomLevelIssues}
        onOpenAssignment={openAssignment}
      />
      <RoomIssueDialog
        isOpen={dialogIssueId != null}
        issueId={dialogIssueId ?? undefined}
        submitIdHint={dialogSubmitIdHint}
        clientIdHint={dialogClientIdHint}
        onClose={closeDialog}
        onSaved={refreshDataRoomLevelIssues}
      />
    </>
  );
};

export const IssuesRoomTab = () => {
  const [subTab, setSubTab] = useState<'active' | 'archived'>('active');

  return (
    <Stack paddingTop={3} gap={2}>
      <Tabs
        value={subTab}
        onChange={(_, value) => setSubTab(value)}
        sx={{ borderBottom: '1px solid #eee' }}>
        <Tab label="Location Issues" value="active" />
        <Tab label="Archived Location Issues" value="archived" />
      </Tabs>
      <IssuesRoomTabContent view={subTab} />
    </Stack>
  );
};
